import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {buildTeamNamespace, findSelectedFilterForTeam, isDemoInbox} from '../../../../helpers/modelHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {StartGuideTaskTypesEnum} from '../../../../models/api/preferencesModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {AppState} from '../../../../store/appState';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {getUserParticipatingChannels} from '../../../../store/data/channels/channelsSelectors';
import {
  getUserOwnedInboxes,
  getUserTeamParticipatingInboxes,
} from '../../../../store/data/inboxes/inboxesSelectors';
import {getSelectedFilters} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {updateStartGuideTasksAction} from '../../../../store/data/preferences/preferencesActions';
import {getCompletedStartGuideTasks} from '../../../../store/data/preferences/preferencesSelectors';
import {getHasTeammates, getIsUserGlobalAdmin} from '../../../../store/data/teammates/teammatesSelectors';
import {getLastUpdatedTeam} from '../../../../store/data/teams/teamsSelectors';
import {
  getHasCellsEmptySetupActions,
  getHasNewTeammateInvite,
} from '../../../../store/featureFlags/featureFlagsSelectors';
import {
  CellsPanelPageParametersSelector,
  CellsPanelPageSelector,
  makeGetCellsPanelCurrentPage,
} from '../../../../store/panels/cells/cellsPanelSelectors';
import {CellsContext} from '../../../../store/panels/cells/context/cellsContext';
import {
  InboxesParametersTypesEnum,
  InboxesSearchParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {CellsPanelId} from '../../../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {searchByParametersAction, SearchInteractionOptions} from '../../../../store/stateless/searchActions';
import {CellsEmptySetupActionsVariantsEnum} from '../../cells/content/cellsEmpty/cellsEmptyCommon';
import {EmptyInboxes} from './emptyInboxes';

/*
 * Props.
 */

interface EmptyInboxesContainerProps {
  panelId: CellsPanelId;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: AppState, props: EmptyInboxesContainerProps) {
  return props.panelId;
}

function makeGetCellsPanelPageParameters(getPage: CellsPanelPageSelector<EmptyInboxesContainerProps>) {
  return createSelector(getPage, (page) => page.parameters);
}
function makeGetCellsPanelPageCellsContext(getPage: CellsPanelPageSelector<EmptyInboxesContainerProps>) {
  return createSelector(getPage, (page) => page.cellsContext);
}

function makeGetShouldShowAddTeamInbox<P>(getParameters: CellsPanelPageParametersSelector<P>) {
  return createSelector(
    [getUserTeamParticipatingInboxes, getHasPermission, getLastUpdatedTeam, getParameters],
    (userTeamParticipatingInboxes, hasPermission, lastUpdatedTeam, parameters) => {
      let namespace: string | undefined;

      // If we are in the user's workspace, team inboxes should be added to the last updated team.
      if (parameters.type === InboxesParametersTypesEnum.USER && lastUpdatedTeam) {
        namespace = lastUpdatedTeam.namespace;
      }
      // If we are in a team workspace, use that team's namespace.
      else if (parameters.type === InboxesParametersTypesEnum.TEAM && parameters.teamId) {
        namespace = buildTeamNamespace(parameters.teamId);
      }

      if (!namespace) {
        return false;
      }

      // If we are in the team workspace, then we do not count the demo inbox as a team inbox,
      // so when users archive the demo inbox the CellsEmptySetupActions will be displayed.
      const hasInbox = Boolean(
        userTeamParticipatingInboxes.find((i) => {
          if (parameters.type === InboxesParametersTypesEnum.TEAM) {
            return i.namespace === namespace && !isDemoInbox(i);
          }

          return i.namespace === namespace;
        }),
      );

      // Show the add team inbox prompt for a team (represented by namespace) if the user has permission to modify the team,
      // and that team has no inboxes accessible to the user.
      return hasPermission(namespace, PermissionsEnum.CREATE_INBOX) && !hasInbox;
    },
  );
}

function makeGetShouldAddChannelToDemoInbox<P>(getParameters: CellsPanelPageParametersSelector<P>) {
  return createSelector(
    [getParameters, getUserTeamParticipatingInboxes, getUserParticipatingChannels, getHasPermission],
    (cellsRouteParameters, userTeamParticipatingInboxes, userParticipatingChannels, hasPermission) => {
      // If we haven't selected an inbox workspace, return.
      if (cellsRouteParameters.type !== InboxesParametersTypesEnum.INBOX || !cellsRouteParameters.inboxId) {
        return false;
      }

      const inbox = userTeamParticipatingInboxes.find((i) => i.id === cellsRouteParameters.inboxId);

      if (!inbox) {
        return false;
      }

      // If we're in a demo inbox workspace, that inbox has no channels, and the user has persmission to add channels,
      // we should prompt the user to do so.
      return (
        isDemoInbox(inbox) &&
        !userParticipatingChannels.find((c) => c.inboxId === inbox.id) &&
        hasPermission(inbox.namespace, PermissionsEnum.CREATE_INBOX)
      );
    },
  );
}

export function makeGetSetupActionsVariant<P>(getParameters: CellsPanelPageParametersSelector<P>) {
  return createSelector(
    [
      getUserOwnedInboxes,
      makeGetShouldShowAddTeamInbox(getParameters),
      makeGetShouldAddChannelToDemoInbox(getParameters),
      getHasTeammates,
      getIsUserGlobalAdmin,
      getCompletedStartGuideTasks,
      getParameters,
      getHasCellsEmptySetupActions,
    ],
    (
      userOwnedInboxes,
      shouldShowAddTeamInbox,
      shouldAddChannelToDemoInbox,
      hasTeammates,
      isUserGlobalAdmin,
      completedStartGuideTasks,
      parameters,
      hasCellsEmptySetupActions,
    ) => {
      if (!hasCellsEmptySetupActions) {
        return null;
      }

      const {type} = parameters;
      const shouldAddIndividualInbox = userOwnedInboxes.every((inbox) => isDemoInbox(inbox));

      // If we are in the teammate workspace, have no user owned inboxes, and
      // have not dismissed the TEAMMATE setup actions, return TEAMMATE variant.
      if (
        (type === InboxesParametersTypesEnum.USER || type === InboxesParametersTypesEnum.TEAMMATE_INBOX) &&
        shouldAddIndividualInbox &&
        !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAMMATE_DISMISS)
      ) {
        return CellsEmptySetupActionsVariantsEnum.TEAMMATE;
      }

      // If we are in the team workspace and should add a team inbox,
      // or are in an demo inbox workspace and should add a channel,
      // and have not dismissed the TEAM setup actions, return TEAM variant.
      if (
        ((type === InboxesParametersTypesEnum.TEAM && shouldShowAddTeamInbox) ||
          (type === InboxesParametersTypesEnum.INBOX && shouldAddChannelToDemoInbox)) &&
        !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAM_DISMISS)
      ) {
        return CellsEmptySetupActionsVariantsEnum.TEAM;
      }

      // If we are in the teammates workspace, have no teammates, are a global
      // admin, and have not dismissed the TEAMMATES setup actions, return TEAMMATES variant.
      if (
        type === InboxesParametersTypesEnum.GLOBAL &&
        !hasTeammates &&
        isUserGlobalAdmin &&
        !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAMMATES_DISMISS)
      ) {
        return CellsEmptySetupActionsVariantsEnum.TEAMMATES;
      }

      return null;
    },
  );
}

function makeGetSelectedTeamDemoInbox(getParameters: ReturnType<typeof makeGetCellsPanelPageParameters>) {
  return createSelector(
    [getParameters, getUserTeamParticipatingInboxes],
    (parameters, userTeamParticipatingInboxes) => {
      // If we're not in an inbox workspace, we can't have selected a demo inbox.
      if (parameters.type !== InboxesParametersTypesEnum.INBOX || !parameters.inboxId) {
        return undefined;
      }

      // Find the selected inbox.
      const selectedInbox = userTeamParticipatingInboxes.find((i) => i.id === parameters.inboxId);

      // Confirm that this inbox is a demo inbox.
      if (!selectedInbox || !isDemoInbox(selectedInbox)) {
        return undefined;
      }

      return selectedInbox;
    },
  );
}

const getSelectedTeamFilter = createSelector(
  [getLastUpdatedTeam, getSelectedFilters],
  (team, selectedFilters) => {
    if (!team) {
      return undefined;
    }

    return findSelectedFilterForTeam(selectedFilters, team.id);
  },
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<EmptyInboxesContainerProps>()(() => {
  const getCellsPanelCurrentPage = makeGetCellsPanelCurrentPage(getCellsPanelId);
  const getCellsPanelPageParameters = makeGetCellsPanelPageParameters(getCellsPanelCurrentPage);

  return {
    ctaVariant: makeGetSetupActionsVariant(getCellsPanelPageParameters),
    shouldShowAddTeamInbox: makeGetShouldShowAddTeamInbox(getCellsPanelPageParameters),
    selectedTeamDemoInbox: makeGetSelectedTeamDemoInbox(getCellsPanelPageParameters),
    lastUpdatedTeam: getLastUpdatedTeam,
    selectedFilter: getSelectedTeamFilter,
    hasNewTeammateInvite: getHasNewTeammateInvite,
    inboxesParameters: getCellsPanelPageParameters,
    cellsContext: makeGetCellsPanelPageCellsContext(getCellsPanelCurrentPage),
  };
});

const mapDispatchToProps = {
  navigateToLinkAction,
  searchByParametersAction,
  updateStartGuideTasksAction,
};

export const EmptyInboxesContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const navigate = (destination: string) => () => dispatchProps.navigateToLinkAction(destination);
    const navigateToSearch = (
      searchParameters: InboxesSearchParameters,
      cellsContext?: CellsContext,
      options?: SearchInteractionOptions,
    ) => dispatchProps.searchByParametersAction(searchParameters, cellsContext, options);
    const dismissCellsEmtpySetupActionsVariant = (task: StartGuideTaskTypesEnum) => () =>
      dispatchProps.updateStartGuideTasksAction(task);

    const teamId = stateProps.lastUpdatedTeam && stateProps.lastUpdatedTeam.id;

    return {
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      teamId,
      navigate,
      navigateToSearch,
      dismissCellsEmtpySetupActionsVariant,
    };
  },
)(EmptyInboxes);
